import React, { useEffect, useState, useMemo } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { DataTable } from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

// Tailor/Konveksi = Supplier dengan partner_type "konveksi" (dipakai di Order Jahit/Produksi).
const KONVEKSI = "konveksi";
const BLANK = { name: "", code: "", phone: "", address: "", notes: "", active: true, partner_types: [KONVEKSI] };

export default function Tailor() {
  const { can } = useAuth();
  const write = can("owner", "warehouse_admin", "purchasing", "production_admin");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [qInput, setQInput] = useState(""); const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ sort: "name", dir: "asc" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true); setError(false);
    api.get("/suppliers", { params: { partner_type: KONVEKSI } })
      .then((r) => setAll(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => { setQ(qInput); setPage(1); }, 300); return () => clearTimeout(t); }, [qInput]);

  const filtered = useMemo(() => {
    let x = all;
    if (status === "active") x = x.filter((s) => s.active);
    else if (status === "inactive") x = x.filter((s) => !s.active);
    if (q) { const t = q.toLowerCase(); x = x.filter((s) => [s.name, s.code, s.phone].some((v) => (v || "").toLowerCase().includes(t))); }
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...x].sort((a, b) => { const av = (a[sort.sort] || "").toString().toLowerCase(), bv = (b[sort.sort] || "").toString().toLowerCase(); return av > bv ? dir : av < bv ? -dir : 0; });
  }, [all, status, q, sort]);

  const total = filtered.length;
  const pages = Math.max(Math.ceil(total / limit), 1);
  const curPage = Math.min(page, pages);
  const rows = filtered.slice((curPage - 1) * limit, curPage * limit);
  const onSort = (key) => { setSort((s) => ({ sort: key, dir: s.sort === key && s.dir === "asc" ? "desc" : "asc" })); setPage(1); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    setSaving(true);
    try {
      const payload = { ...form, partner_types: Array.from(new Set([...(form.partner_types || []), KONVEKSI])) };
      if (form.id) await api.put(`/suppliers/${form.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success("Tersimpan"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Nonaktifkan tailor/konveksi "${t.name}"? (data mitra tetap tersimpan untuk histori Order Jahit)`)) return;
    try { await api.put(`/suppliers/${t.id}`, { ...t, active: false, partner_types: Array.from(new Set([...(t.partner_types || []), KONVEKSI])) }); toast.success("Dinonaktifkan"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const columns = [
    { key: "name", label: "Nama", sortable: true, render: (r) => <span className="font-medium text-cocoa">{r.name}</span> },
    { key: "code", label: "Kode", sortable: true, render: (r) => r.code || "-" },
    { key: "phone", label: "No. HP", sortable: true, render: (r) => r.phone || "-" },
    { key: "address", label: "Alamat", render: (r) => <span className="text-sm text-wmuted">{r.address || "-"}</span> },
    { key: "active", label: "Status", align: "center", render: (r) => r.active
        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Aktif</Badge>
        : <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200">Nonaktif</Badge> },
    { key: "notes", label: "Catatan", render: (r) => <span className="text-sm text-wmuted">{r.notes || "-"}</span> },
    ...(write ? [{ key: "_act", label: "Aksi", align: "right", render: (r) => (
      <div className="flex justify-end gap-1">
        <Button size="icon" variant="ghost" className="h-8 w-8 text-cocoa" onClick={() => setForm({ ...r })} data-testid={`tailor-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
        {r.active && <Button size="icon" variant="ghost" className="h-8 w-8 text-status-reject" onClick={() => del(r)} data-testid={`tailor-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
      </div>
    ) }] : []),
  ];

  return (
    <div data-testid="tailor-page">
      <PageHeader title="Tailor / Konveksi" subtitle="Master mitra konveksi — dipakai saat kirim Order Jahit ke konveksi">
        {write && <Button onClick={() => setForm({ ...BLANK })} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="tailor-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Tambah</Button>}
      </PageHeader>

      <Panel className="p-4">
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Cari nama / kode / no. HP..." className="w-72 bg-white" data-testid="tailor-search" />
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-40 bg-white" data-testid="tailor-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="active">Aktif</SelectItem>
              <SelectItem value="inactive">Nonaktif</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DataTable
          testid="tailor-table" columns={columns} rows={rows} loading={loading} error={error}
          sort={sort} onSort={onSort} page={curPage} pages={pages} total={total} limit={limit}
          onPageChange={setPage} onLimitChange={(n) => { setLimit(n); setPage(1); }}
          searching={loading && !!q} emptyLabel="Belum ada konveksi. Klik Tambah untuk menambahkan mitra konveksi."
        />
      </Panel>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="tailor-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit" : "Tambah"} Tailor / Konveksi</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tailor-f-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kode</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="otomatis bila kosong" data-testid="tailor-f-code" /></div>
                <div><Label>No. HP</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tailor-f-phone" /></div>
              </div>
              <div><Label>Alamat</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="tailor-f-address" /></div>
              <div><Label>Catatan</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="tailor-f-note" /></div>
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="tailor-f-active" /><Label className="mb-0">Aktif</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} data-testid="tailor-cancel">Batal</Button>
            <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="tailor-save">{saving ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
